// Reports the two Flappy Bird counters to the auth service.
//
// Every other game has a backend that reports on its behalf. This one is pure
// client-side canvas, so the browser is the only thing that can say a game was
// played. The endpoint accepts a two-item allowlist precisely because it is the
// one unauthenticated write path on the site.
//
// Two rules, the same ones the backend emitters follow:
//
//   1. Never throw at the caller. A missed counter is nothing; an exception in
//      the phase handler would break the game.
//   2. Never delay the game. Nothing here is awaited.

/** Same-origin: Caddy proxies /api to the auth service and leaves this path open. */
const ENDPOINT = '/api/beacon';
const GAME = 'flappy-bird';

function post(type: string, durationMs?: number): void {
  const payload: Record<string, unknown> = { game: GAME, type };
  if (durationMs !== undefined) payload.durationMs = Math.round(durationMs);
  const body = JSON.stringify(payload);

  try {
    // sendBeacon survives the page being closed, which is exactly when a run
    // ends: the player dies and shuts the tab. A fetch would be cancelled.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* a lost counter is not worth surfacing to the player */
    });
  } catch {
    /* sendBeacon throws on some payload/permission combinations; ignore */
  }
}

export function reportPlay(): void {
  post('flappy_play');
}

/**
 * `durationMs` is how long the run lasted. Score is deliberately not sent: the
 * collector's schema has no field for it, and squeezing it into one that means
 * something else would put misleading numbers in the dashboard.
 */
export function reportGameOver(durationMs: number): void {
  post('flappy_game_over', durationMs);
}
